// Flexible Workflow Configuration System
// Allows easy customization of workflow steps, buttons, and logic

export const WORKFLOW_TYPES = {
  CBS: 'cbs',
  CAP_HIGH: 'cap_high',
  CAP_MEDIUM: 'cap_medium'
};

export const ROLES = {
  CB: 'CB', // Cán bộ
  QLKH: 'QLKH', // Quản lý khách hàng
  TRUONG_PHONG: 'Trưởng phòng',
  ADMIN: 'Admin'
};

export const APPROVAL_STATUS = {
  PENDING: 'pending',
  APPROVED: 'approved',
  REJECTED: 'rejected',
  AGREE: 'agree',
  DISAGREE: 'disagree'
};

// CBS Workflow Configuration (6 steps - bỏ Nhập dữ liệu & Lưu dữ liệu vì KH đã nhập trước)
export const CBS_WORKFLOW = {
  id: 'cbs',
  name: 'Workflow CBS - Điều tra khách hàng',
  steps: [
    {
      id: 'cbs_1',
      order: 1,
      name: 'Chọn khách hàng',
      description: 'Click vào tên khách hàng trong danh sách',
      buttonLabel: null,
      requiredRole: [ROLES.CB, ROLES.QLKH, ROLES.TRUONG_PHONG, ROLES.ADMIN],
      action: 'select_customer',
      nextStep: 'cbs_4'
    },
    {
      id: 'cbs_4',
      order: 2,
      name: 'Điều tra khách hàng CBS',
      description: 'Chuyển sang thực hiện điều tra khách hàng trong CBS',
      buttonLabel: 'Điều tra khách hàng CBS',
      requiredRole: [ROLES.CB, ROLES.QLKH, ROLES.ADMIN],
      action: 'start_investigation',
      nextStep: 'cbs_5'
    },
    {
      id: 'cbs_5',
      order: 3,
      name: 'Đồng ý thực hiện CAS',
      description: 'Quyết định có thực hiện CAS (Credit Analysis System) hay không',
      buttonLabel: 'Đồng ý thực hiện CAS',
      requiredRole: [ROLES.CB, ROLES.QLKH, ROLES.ADMIN],
      action: 'approve_cas',
      requiresComment: true,
      options: ['Đồng ý', 'Không đồng ý'],
      nextStep: 'cbs_6'
    },
    {
      id: 'cbs_6',
      order: 4,
      name: 'Rà soát kết quả CAS',
      description: 'Rà soát kết quả điều tra CAS',
      buttonLabel: 'Đồng ý kết quả CAS',
      requiredRole: [ROLES.QLKH, ROLES.TRUONG_PHONG, ROLES.ADMIN],
      action: 'review_cas',
      options: ['Đồng ý', 'Không đồng ý'],
      requiresComment: true,
      nextStep: 'cbs_7'
    },
    {
      id: 'cbs_7',
      order: 5,
      name: 'Phê duyệt kết quả CAS',
      description: 'Cấp quản lý phê duyệt kết quả CAS',
      buttonLabel: 'Phê duyệt kết quả CAS',
      requiredRole: [ROLES.TRUONG_PHONG, ROLES.ADMIN],
      action: 'approve_cas_result',
      options: ['Phê duyệt', 'Từ chối'],
      requiresComment: true,
      nextStep: 'cbs_8'
    },
    {
      id: 'cbs_8',
      order: 6,
      name: 'Chuyển sang CAP',
      description: 'Chuyển sang giai đoạn CAP (Corrective Action Plan)',
      buttonLabel: 'Chuyển sang CAP',
      requiredRole: [ROLES.CB, ROLES.QLKH, ROLES.ADMIN],
      action: 'move_to_cap',
      nextStep: null, // Will transition to CAP workflow
      nextWorkflow: 'cap'
    }
  ]
};

// CAP Options Configuration
export const CAP_OPTIONS = [
  { id: 'ban_xu_ly', label: 'Bản xử lý', category: 'action' },
  { id: 'khoi_kien', label: 'Khởi kiện', category: 'action' },
  { id: 'co_cau_no', label: 'Cơ cấu nợ', category: 'action' },
  { id: 'yeu_cau_bo_sung', label: 'Yêu cầu bổ sung tài sản đảm bảo', category: 'action' },
  { id: 'xu_ly_cap_dan_bao', label: 'Xử lý tại cấp dân bảo', category: 'action' },
  { id: 'kiem_tra_thuc_te', label: 'Kiểm tra thực tế', category: 'action' }
];

export const CAP_CLASSIFICATION = {
  ban_xln: ['CB', 'QLKH'], // Ban xử lý nợ
  thap: ['Trung bình', 'Cao'] // Tháp xử lý
};

// CAP Workflow Configuration - High Risk (Luồng 1.1 - 7 steps)
export const CAP_HIGH_WORKFLOW = {
  id: 'cap_high',
  name: 'Workflow CAP - Rủi ro Cao',
  riskLevel: 'high',
  steps: [
    {
      id: 'cap_9',
      order: 9,
      name: 'Chọn phương án CAP',
      description: 'Chọn các phương án xử lý và phân loại',
      buttonLabel: 'CHUYỂN TIẾP',
      requiredRole: [ROLES.CB, ROLES.QLKH, ROLES.ADMIN],
      action: 'select_cap_options',
      formFields: ['cap_options', 'ban_xln', 'thap_level'],
      capOptions: CAP_OPTIONS,
      classification: CAP_CLASSIFICATION,
      nextStep: 'cap_10'
    },
    {
      id: 'cap_10',
      order: 10,
      name: 'Đồng ý đề xuất CAP',
      description: 'Rà soát đề xuất phương án CAP',
      buttonLabel: 'Đồng ý đề xuất CAP',
      requiredRole: [ROLES.QLKH, ROLES.TRUONG_PHONG, ROLES.ADMIN],
      action: 'review_cap_proposal',
      options: ['Đồng ý', 'Không đồng ý'],
      requiresComment: true,
      nextStep: 'cap_11'
    },
    {
      id: 'cap_11',
      order: 11,
      name: 'Phê duyệt đề xuất CAP',
      description: 'Cấp quản lý phê duyệt phương án CAP',
      buttonLabel: 'Phê duyệt đề xuất CAP',
      requiredRole: [ROLES.TRUONG_PHONG, ROLES.ADMIN],
      action: 'approve_cap_proposal',
      options: ['Phê duyệt', 'Từ chối'],
      requiresComment: true,
      nextStep: 'cap_12'
    },
    {
      id: 'cap_12',
      order: 12,
      name: 'Triển khai biện pháp',
      description: 'Thực hiện biện pháp khắc phục và đính kèm file kết quả',
      buttonLabel: 'Triển khai biện pháp khắc phục',
      requiredRole: [ROLES.CB, ROLES.QLKH, ROLES.ADMIN],
      action: 'implement_cap',
      requiresFileUpload: true,
      requiresComment: true,
      nextStep: 'cap_13'
    },
    {
      id: 'cap_13',
      order: 13,
      name: 'Đồng ý kết quả CAP',
      description: 'Rà soát kết quả thực hiện CAP',
      buttonLabel: 'Đồng ý kết quả CAP',
      requiredRole: [ROLES.QLKH, ROLES.TRUONG_PHONG, ROLES.ADMIN],
      action: 'review_cap_result',
      options: ['Đồng ý', 'Không đồng ý'],
      requiresComment: true,
      nextStep: 'cap_14'
    },
    {
      id: 'cap_14',
      order: 14,
      name: 'Phê duyệt kết quả CAP',
      description: 'Cấp quản lý phê duyệt kết quả thực hiện',
      buttonLabel: 'Phê duyệt kết quả CAP',
      requiredRole: [ROLES.TRUONG_PHONG, ROLES.ADMIN],
      action: 'approve_cap_result',
      options: ['Phê duyệt', 'Từ chối'],
      requiresComment: true,
      nextStep: 'cap_15'
    },
    {
      id: 'cap_15',
      order: 15,
      name: 'Đóng trường hợp',
      description: 'Đóng trường hợp khách hàng trong CBS',
      buttonLabel: 'Đóng trường hợp CBS',
      requiredRole: [ROLES.QLKH, ROLES.TRUONG_PHONG, ROLES.ADMIN],
      action: 'close_case',
      confirmAction: 'Đồng ý đóng trường hợp CBS',
      requiresComment: true,
      nextStep: null // End of workflow
    }
  ]
};

// CAP Medium/Low Risk - Simplified workflow
export const CAP_MEDIUM_WORKFLOW = {
  id: 'cap_medium',
  name: 'Workflow CAP - Rủi ro Trung bình/Thấp',
  riskLevel: 'medium',
  steps: [
    {
      id: 'cap_m1',
      order: 1,
      name: 'Chọn phương án CAP',
      description: 'Chọn các phương án xử lý đơn giản',
      buttonLabel: 'CHUYỂN TIẾP',
      requiredRole: [ROLES.CB, ROLES.QLKH, ROLES.ADMIN],
      action: 'select_cap_options',
      formFields: ['cap_options', 'notes'],
      capOptions: CAP_OPTIONS,
      nextStep: 'cap_m2'
    },
    {
      id: 'cap_m2',
      order: 2,
      name: 'Phê duyệt và thực hiện',
      description: 'Phê duyệt nhanh và triển khai',
      buttonLabel: 'Phê duyệt và Triển khai',
      requiredRole: [ROLES.QLKH, ROLES.ADMIN],
      action: 'approve_and_implement',
      requiresComment: true,
      nextStep: 'cap_m3'
    },
    {
      id: 'cap_m3',
      order: 3,
      name: 'Đóng trường hợp',
      description: 'Đóng case đơn giản',
      buttonLabel: 'Đóng trường hợp',
      requiredRole: [ROLES.QLKH, ROLES.ADMIN],
      action: 'close_case',
      requiresComment: true,
      nextStep: null
    }
  ]
};

// Helper functions
export const getWorkflowByType = (type) => {
  switch(type) {
    case WORKFLOW_TYPES.CBS:
      return CBS_WORKFLOW;
    case WORKFLOW_TYPES.CAP_HIGH:
      return CAP_HIGH_WORKFLOW;
    case WORKFLOW_TYPES.CAP_MEDIUM:
      return CAP_MEDIUM_WORKFLOW;
    default:
      return CBS_WORKFLOW;
  }
};

export const getStepById = (workflowType, stepId) => {
  const workflow = getWorkflowByType(workflowType);
  return workflow.steps.find(step => step.id === stepId);
};

export const canUserAccessStep = (step, userRole) => {
  if (!step || !step.requiredRole) return true;
  return step.requiredRole.includes(userRole);
};

export const getNextStep = (currentStep, workflowType) => {
  const workflow = getWorkflowByType(workflowType);
  const currentStepIndex = workflow.steps.findIndex(s => s.id === currentStep.id);
  
  if (currentStepIndex < workflow.steps.length - 1) {
    return workflow.steps[currentStepIndex + 1];
  }
  
  return null;
};
